import { useCallback } from 'react';
import { getHotkeyHandler } from '@mantine/hooks';
import { m } from 'motion/react';
import type { SuggestedCTA } from './services/types';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { navigateTo } from './utils/url';
import { usePlaygroundCtaInterceptor } from './hooks/usePlaygroundCtaInterceptor';

type CTAButtonsProps = {
  primaryCta?: SuggestedCTA;
  secondaryCta?: SuggestedCTA;
};

export function CTAButtons({ primaryCta, secondaryCta }: CTAButtonsProps) {
  const theme = useSuperbarTheme();
  const { interceptCtaClick } = usePlaygroundCtaInterceptor();

  const handleCtaClick = useCallback(
    (cta: SuggestedCTA) => {
      const href = cta.defaultAction?.actionProps?.href;
      if (href) {
        if (interceptCtaClick(href)) return;
        navigateTo(href);
      }
    },
    [interceptCtaClick]
  );

  if (!primaryCta && !secondaryCta) {
    return null;
  }

  return (
    <div className="caddietw-flex caddietw-gap-2">
      {primaryCta && (
        <m.div
          initial={{ opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.35,
            delay: 0.05,
            ease: [0.22, 1, 0.36, 1],
          }}
          role="button"
          tabIndex={0}
          onClick={() => handleCtaClick(primaryCta)}
          onKeyDown={getHotkeyHandler([
            ['Enter', () => handleCtaClick(primaryCta)],
            ['Space', () => handleCtaClick(primaryCta)],
          ])}
          whileHover={{ opacity: 0.5 }}
          className="caddietw-px-5 caddietw-py-2 caddietw-rounded-full caddietw-text-sm caddietw-font-semibold caddietw-border caddietw-border-transparent caddietw-cursor-pointer caddietw-transition-all"
          style={{
            backgroundColor:
              theme.components.assistantMessage.ctaButton.background,
            color: theme.components.assistantMessage.ctaButton.text,
          }}
        >
          {primaryCta.name}
        </m.div>
      )}
      {secondaryCta && (
        <m.div
          initial={{ opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.35,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1],
          }}
          role="button"
          tabIndex={0}
          onClick={() => handleCtaClick(secondaryCta)}
          onKeyDown={getHotkeyHandler([
            ['Enter', () => handleCtaClick(secondaryCta)],
            ['Space', () => handleCtaClick(secondaryCta)],
          ])}
          whileHover={{ opacity: 0.5 }}
          className="caddietw-px-5 caddietw-py-2 caddietw-bg-white caddietw-border caddietw-rounded-full caddietw-text-sm caddietw-font-semibold caddietw-cursor-pointer caddietw-transition-all"
          style={{
            borderColor: 'transparent',
            color: theme.colors.ink.primary,
          }}
        >
          {secondaryCta.name}
        </m.div>
      )}
    </div>
  );
}
